import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { getThisWeek, getWeek } from "../utils/time";
import CustomLoader from "./CustomLoader";
import { IProjectResults, ITimeEntry, IUser, IWeekData, IDefaultHoursDay, IProjectHourTotals } from "../types/types";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogActions,
    Button,
    Input,
    Text,
    Checkbox,
} from "@fluentui/react-components";
import { Settings20Regular } from "@fluentui/react-icons";
import WeektableNavigation from "./weektable/WeektableNavigation";
import WeektableDataRows from "./weektable/WeektableDataRows";
import * as Constants from "../utils/constants";
import SpinnerOverlay from "./SpinnerOverlay";
import { ApiContext } from "../providers/ApiProvider";
import { useRealtimeTimeEntries } from "../hooks/useRealtimeTimeEntries";
import offcodeLogo from "../images/favicon.ico";
import BulkFillHoursDialog from "./BulkFillHoursDialog";

interface IWeekTableInterface {
    token: string;
    user: IUser;
}

export interface IDropdownElement {
    label: string; // company name
    value: string; // project name
    project_id: string;
    image: any;
    key: string;
    private: boolean;
    /** Optional project note/description for tooltips in selectors */
    note?: string | null;
    __isNew__?: boolean;
}

// Weektable component
const WeekTable = (props: IWeekTableInterface) => {
    const {
        fetchProjects,
        getUser,
        getTimeEntriesBetween,
        getProjectHourTotals,
        getDefaultHoursWeek,
        setDefaultHoursDayMode,
        overrideDefaultHoursForDay,
        resetDefaultHoursOverrideForDay,
        updateUserAppSettings,
        getDefaultHoursConfigurationForWeek,
        debugSimulateTokenExpired,
    } = useContext(ApiContext);

    const [field, setField] = useState<IWeekData["dates"]>();
    const [dayTotal, setDayTotal] = useState<{ [x: string]: number }>({});
    const [selectedDays, setSelectedDays] = useState<number[]>([]);
    const [dbHours, setDbHours] = useState<ITimeEntry[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [changingWeek, setChangingWeek] = useState<boolean>(false);
    const [weekTotal, setWeekTotal] = useState<IWeekData["weekTotal"]>();
    const [projectHourTotals, setProjectHourTotals] = useState<IProjectHourTotals["totals"]>({});
    const [totalsPeriod, setTotalsPeriod] = useState<{ year: number; month: number } | null>(null);
    const [user, setUser] = useState<IUser | undefined>();
    const [visibleProjects, setVisibleProjects] = useState<string[] | undefined>();
    const [overflow, setOverflow] = useState<"auto" | "visible">("visible");
    const [projectsArr, setProjectsArr] = useState<string[]>([""]);
    const [dropdownArr, setDropdownArr] = useState<IDropdownElement[] | undefined>();
    const [projectsData, setProjectsData] = useState<IProjectResults[] | undefined>();
    const [loadingCells, setLoadingCells] = useState<{ [key: string]: boolean }>({});
    const loadingCellsRef = useRef<{ [key: string]: boolean }>({});
    const focusedCellsRef = useRef<{ [key: string]: boolean }>({});
    const [changedCells, setChangedCells] = useState<{ [key: string]: boolean }>({});
    // Track local unsaved changes (cells that have been modified but not yet saved)
    const localChangesRef = useRef<{ [key: string]: string }>({});

    // Default hours day info per date (keyed by day timestamp) for currently selected week
    const [defaultHoursByDate, setDefaultHoursByDate] = useState<{ [date: number]: IDefaultHoursDay }>({});
    // Simple in-memory cache for default hours by date across weeks to avoid refetching
    const defaultHoursCacheRef = useRef<{ [date: number]: IDefaultHoursDay }>({});
    // Cached default-project-per-day for the current week (invalidated on week change / user settings)
    const defaultProjectConfigCacheRef = useRef<{
        weekKey: string;
        defaultProjectIdByDate: { [date: number]: string | null };
    } | null>(null);
    // Per-day default project (source of truth for "default project" in UI)
    const [defaultProjectIdByDate, setDefaultProjectIdByDate] = useState<{ [date: number]: string | null }>({});
    const [isDefaultHoursDialogOpen, setIsDefaultHoursDialogOpen] = useState(false);
    const [defaultHoursDrafts, setDefaultHoursDrafts] = useState<{ [date: number]: string }>({});

    // Snapshot of the last fully ready table data.
    // While a new week is loading, we keep rendering this snapshot under the spinner
    // so rows do not jump through intermediate states.
    const [stableTableSnapshot, setStableTableSnapshot] = useState<{
        dayTotal: { [x: string]: number };
        dbHours: ITimeEntry[];
        weekTotal: IWeekData["weekTotal"] | undefined;
        field: IWeekData["dates"] | undefined;
        selectedDays: number[];
        projectsData: IProjectResults[] | undefined;
        dropdownArr: IDropdownElement[] | undefined;
        projectsArr: string[];
    } | null>(null);

    // Week options dialog (mobile/desktop)
    const [isWeekOptionsDialogOpen, setIsWeekOptionsDialogOpen] = useState(false);
    // Default to false; backend flag show_only_projects_with_hours will override when present
    const [showOnlyWithHours, setShowOnlyWithHours] = useState(false);

    const fieldRef = useRef<IWeekData["dates"] | undefined>(undefined);

    // Keep refs in sync with state
    useEffect(() => {
        loadingCellsRef.current = loadingCells;
    }, [loadingCells]);

    useEffect(() => {
        fieldRef.current = field;
    }, [field]);

    // Wrapper for setField that updates fieldRef synchronously
    const setFieldWithRef = useCallback(
        (updater: IWeekData["dates"] | ((prev: IWeekData["dates"] | undefined) => IWeekData["dates"] | undefined)) => {
            if (typeof updater === "function") {
                // For function updaters, we need to call it with current ref value to get new value
                const currentField = fieldRef.current;
                const newField = updater(currentField);
                // Update ref synchronously BEFORE React processes the state update
                fieldRef.current = newField;
                setField(newField);
            } else {
                // For direct values, update ref synchronously BEFORE React processes the state update
                fieldRef.current = updater;
                setField(updater);
            }
        },
        [],
    );

    // Component mounts
    useEffect(() => {
        initialFetch();
    }, []);

    // Resize event handler
    useEffect(() => {
        const handleResize = () => {
            setOverflow(window.innerWidth >= Constants.MOBILEWIDTH ? "visible" : "auto");
        };

        // Set initial value
        handleResize();

        // Add event listener
        window.addEventListener("resize", handleResize);

        // Cleanup
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const initialFetch = async () => {
        try {
            await fetchUser().then((activeProjects) => {
                _fetchProjects(activeProjects); //fetch project
                setVisibleProjects(activeProjects);
            });

            const date = new Date();
            const thisWeek = getThisWeek(date);
            if (selectedDays !== thisWeek) setSelectedDays(thisWeek);
            await fetchTimeEntries(thisWeek, { loadDefaultProjectConfig: true });
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    //TODO: This is horrible call. This takse long time and should not be like that. Fix me PLS
    const _fetchProjects = async (activeProjects: string[] | undefined) => {
        try {
            const listOfProjects: any = [];
            const projects: IProjectResults[] = await fetchProjects(props.token);
            // console.log("Fetched projects:", projects);
            const newArr: IDropdownElement[] = [];

            if (projects.length > 0) {
                for (const project of projects) {
                    // console.log("Processing project:", project.id, "in activeProjects?", activeProjects?.includes(project.id!));

                    // Add project to listOfProjects if it's visible (should be shown in weektable)
                    if (activeProjects && activeProjects.includes(project.id!)) {
                        const projectKey = `${project.company_name}-${project.project_name}_${project.id}`;
                        // console.log("Adding visible project to listOfProjects:", projectKey);
                        listOfProjects.push(projectKey);
                    }

                    // Add project to dropdown if it's NOT visible (can be added to visible projects)
                    if (activeProjects) {
                        // Check that list does not include items that are already selected to visibleProjects array
                        if (!activeProjects.some((val) => val === project.id)) {
                            newArr.push({
                                label: project.company_name,
                                value: project.project_name,
                                project_id: project.id!,
                                image: offcodeLogo,
                                key: project.company_name + project,
                                private: project.private,
                                note: (project as any).note ?? null,
                            });
                        }
                    }
                }
            }
            // console.log("Final listOfProjects:", listOfProjects);
            // console.log("Final newArr:", newArr);
            setDropdownArr(newArr);
            setProjectsData(projects);
            setProjectsArr(listOfProjects);
        } catch (error) {
            console.error(error);
        }
    };

    // Fetch user and return visible projects
    const fetchUser = async () => {
        try {
            let user: IUser;

            if (props.user === null) {
                user = await getUser(props.token);
            } else {
                user = props.user;
            }
            setUser(user);
            if (user.app_settings && typeof user.app_settings.show_only_projects_with_hours === "boolean") {
                setShowOnlyWithHours(user.app_settings.show_only_projects_with_hours);
            }
            return user.app_settings.visible_projects;
        } catch (error) {
            console.error(error);
        }
    };

    const getWeekDayRange = (week: any[]) => {
        const days = week.slice(1, 8);
        const from = days[0].valueOf();
        const to = days[days.length - 1].valueOf();
        return { days, from, to, weekKey: `${from}-${to}` };
    };

    /** Refresh per-day default-hours runtime state (consumption, auto/manual). */
    const refreshDefaultHoursDays = useCallback(
        async (week: any[]) => {
            if (!week || week.length === 0) return;
            const { days, from, to } = getWeekDayRange(week);

            const weekDays = await getDefaultHoursWeek(props.token, from, to);

            const byDate: { [date: number]: IDefaultHoursDay } = {};
            weekDays.forEach((dayInfo) => {
                const dayDate = dayInfo.date;
                defaultHoursCacheRef.current[dayDate] = dayInfo;
                byDate[dayDate] = dayInfo;
            });

            const defaultHoursMap: { [date: number]: IDefaultHoursDay } = {};
            days.forEach((dayTimestamp: number) => {
                const dayInfo = byDate[dayTimestamp] ?? defaultHoursCacheRef.current[dayTimestamp];
                if (dayInfo) {
                    defaultHoursMap[dayTimestamp] = dayInfo;
                }
            });

            setDefaultHoursByDate(defaultHoursMap);
        },
        [getDefaultHoursWeek, props.token],
    );

    /** Load which project row is the effective default per day (admin configuration). Cached per week. */
    const loadDefaultProjectMapForWeek = useCallback(
        async (week: any[], force = false) => {
            if (!week || week.length === 0) return;
            const { days, from, to, weekKey } = getWeekDayRange(week);

            if (!force && defaultProjectConfigCacheRef.current?.weekKey === weekKey) {
                setDefaultProjectIdByDate(defaultProjectConfigCacheRef.current.defaultProjectIdByDate);
                return;
            }

            const configByDay = await getDefaultHoursConfigurationForWeek(props.token, from, to);

            const defaultProjectMap: { [date: number]: string | null } = {};
            days.forEach((dayTimestamp: number) => {
                const dayStart = new Date(dayTimestamp);
                dayStart.setUTCHours(0, 0, 0, 0);
                const key = String(dayStart.getTime());
                const config =
                    configByDay[key] ??
                    configByDay[String(dayTimestamp)] ??
                    null;
                defaultProjectMap[dayTimestamp] = config ? config.project_id : null;
            });

            defaultProjectConfigCacheRef.current = { weekKey, defaultProjectIdByDate: defaultProjectMap };
            setDefaultProjectIdByDate(defaultProjectMap);
        },
        [getDefaultHoursConfigurationForWeek, props.token],
    );

    const invalidateDefaultProjectConfigCache = useCallback(() => {
        defaultProjectConfigCacheRef.current = null;
    }, []);

    // Handlers for default hours day actions
    const handleDefaultHoursModeChange = async (date: number, mode: "auto" | "manual") => {
        try {
            const updated = await setDefaultHoursDayMode(props.token, date, mode);
            // Update both the visible week state and the cross-week cache
            defaultHoursCacheRef.current[date] = updated;
            setDefaultHoursByDate((prev) => ({
                ...prev,
                [date]: updated,
            }));
            if (selectedDays && selectedDays.length > 0) {
                await fetchTimeEntries(selectedDays);
            }
        } catch (error) {
            console.error("Failed to update default hours mode for day", date, error);
        }
    };

    const handleDefaultHoursOverrideChange = async (date: number, totalHours: number) => {
        try {
            const updated = await overrideDefaultHoursForDay(props.token, date, totalHours);
            // Update both the visible week state and the cross-week cache
            defaultHoursCacheRef.current[date] = updated;
            setDefaultHoursByDate((prev) => ({
                ...prev,
                [date]: updated,
            }));
            if (selectedDays && selectedDays.length > 0) {
                await fetchTimeEntries(selectedDays);
            }
        } catch (error) {
            console.error("Failed to override default hours for day", date, error);
        }
    };

    const handleDefaultHoursReset = async (date: number) => {
        try {
            const updated = await resetDefaultHoursOverrideForDay(props.token, date);
            // Update both the visible week state and the cross-week cache
            defaultHoursCacheRef.current[date] = updated;
            setDefaultHoursByDate((prev) => ({
                ...prev,
                [date]: updated,
            }));
            if (selectedDays && selectedDays.length > 0) {
                await fetchTimeEntries(selectedDays);
            }
        } catch (error) {
            console.error("Failed to reset default hours override for day", date, error);
        }
    };

    // When backend recalculates a day's default hours (e.g. via reset-default-hours),
    // keep our per-day mode/consumption in sync so manual/auto markers update correctly.
    const handleDefaultHoursDayUpdatedFromTimeEntry = (day: IDefaultHoursDay) => {
        const dayDate = day.date;
        defaultHoursCacheRef.current[dayDate] = day;
        setDefaultHoursByDate((prev) => ({
            ...prev,
            [dayDate]: day,
        }));
    };

    // Update project list & time entries
    const update = async (week?: any[] | undefined) => {
        try {
            console.info("Update started");
            await fetchUser().then((activeProjects) => {
                // Refresh project metadata & dropdown options based on the
                // latest visible project ids from the backend.
                // NOTE: `visibleProjects` state is already updated optimistically
                // by the callers that modify visibility (add/hide). Overwriting
                // it here with a slightly stale value from the backend can cause
                // recently added/removed projects to disappear temporarily until
                // the next full reload, so we avoid resetting the state here.
                _fetchProjects(activeProjects);
            });

            const use = week ? week : selectedDays;
            await fetchTimeEntries(use); //fetch time entries
        } catch (error) {
            console.error(error);
        } finally {
            console.info("FINALLY Update done");
        }
    };

    // Get time entries(selected week) and keep default-hours metadata in sync
    const fetchTimeEntries = async (
        week: any[],
        options?: { loadDefaultProjectConfig?: boolean },
    ) => {
        try {
            if (week === undefined || week.length === 0) return;
            const date = {
                from: week[1].valueOf(),
                to: week[8].valueOf(),
            };
            const weekData: IWeekData = await getTimeEntriesBetween(props.token, date);
            setWeekTotal(weekData.weekTotal);
            setDbHours(weekData.timeEntries);

            // Merge new field data with existing field data, preserving all local unsaved changes
            setField((prevField) => {
                // Helper to normalize null/undefined to empty string for all fields
                const sanitizeFieldMap = (map: { [key: string]: any } | undefined): IWeekData["dates"] => {
                    const result: IWeekData["dates"] = {};
                    if (!map) return result;
                    Object.keys(map).forEach((key) => {
                        const value = map[key];
                        result[key] = value == null ? "" : String(value);
                    });
                    return result;
                };

                if (!prevField) {
                    // First-time load: sanitize server data so we never store null in the field map
                    return sanitizeFieldMap(weekData.dates);
                }

                // Use fieldRef to get the most recent field value (may have been updated by onChange)
                const currentField = fieldRef.current || prevField;

                // Get current loading and focused cells to identify which fields have unsaved local changes
                const currentLoadingCells = loadingCellsRef.current;
                const currentFocusedCells = focusedCellsRef.current;

                // Helper function to format numeric values to one decimal place
                const formatNumericValue = (value: string | undefined): string => {
                    if (!value || value === "" || value === "0") return value || "";
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                        return numValue.toFixed(1);
                    }
                    return value;
                };

                // Helper function to check if a field corresponds to a loading or focused cell
                const isProtectedField = (fieldName: string): boolean => {
                    // Check if this field corresponds to a loading cell
                    for (const cellKey in currentLoadingCells) {
                        if (currentLoadingCells[cellKey]) {
                            const [projectID, timestamp] = cellKey.split("-");
                            if (projectID && timestamp) {
                                const cellFieldName = `${projectID}_${timestamp}`;
                                if (cellFieldName === fieldName) return true;
                            }
                        }
                    }
                    // Check if this field corresponds to a focused cell
                    for (const cellKey in currentFocusedCells) {
                        if (currentFocusedCells[cellKey]) {
                            const [projectID, timestamp] = cellKey.split("-");
                            if (projectID && timestamp) {
                                const cellFieldName = `${projectID}_${timestamp}`;
                                if (cellFieldName === fieldName) return true;
                            }
                        }
                    }
                    // Check if this field has local unsaved changes
                    if (localChangesRef.current[fieldName] !== undefined) {
                        return true;
                    }
                    return false;
                };

                // Start with server data as base
                const mergedField: { [key: string]: any } = { ...weekData.dates };

                // Preserve all local field values that differ from server (unsaved local changes)
                // or are protected (loading/focused)
                Object.keys(currentField).forEach((fieldName) => {
                    const localValue = currentField[fieldName] == null ? "" : currentField[fieldName];
                    const rawServerValue = weekData.dates[fieldName] == null ? "" : weekData.dates[fieldName];
                    const formattedServerValue = formatNumericValue(rawServerValue);

                    // Special case: If entry was deleted (rawServerValue is undefined), clear the field
                    // unless it's protected (user is currently editing it)
                    if (rawServerValue === undefined) {
                        if (isProtectedField(fieldName)) {
                            // User is editing this field, preserve their input
                            mergedField[fieldName] = localValue;
                        } else {
                            // Entry was deleted, clear the field
                            mergedField[fieldName] = "";
                        }
                        return;
                    }

                    // Special case: If server has a value but local is empty, use server value
                    // This handles the case where a new entry was created in another window
                    // after the local field was cleared (e.g., delete then add in another window)
                    if (rawServerValue && (!localValue || localValue === "")) {
                        mergedField[fieldName] = formattedServerValue;
                        return;
                    }

                    // Preserve local value ONLY if:
                    // 1. Field is protected (loading or focused), OR
                    // 2. Field has active local unsaved changes (in localChangesRef)
                    // Otherwise, use server value (which may be updated from another window)
                    if (isProtectedField(fieldName)) {
                        // User is actively editing this field, preserve their input
                        mergedField[fieldName] = localValue;
                    } else if (localChangesRef.current[fieldName] !== undefined) {
                        // Field has active local unsaved changes, preserve them
                        mergedField[fieldName] = localValue;
                    } else {
                        // No active local changes - use server value (may be updated from another window)
                        mergedField[fieldName] = formattedServerValue;
                        // Clear local change tracking if value matches server
                        delete localChangesRef.current[fieldName];
                    }
                });

                // Handle new fields from server that don't exist in currentField
                Object.keys(weekData.dates).forEach((fieldName) => {
                    if (!(fieldName in currentField)) {
                        // New field from server, use formatted server value
                        const rawServerValue = weekData.dates[fieldName] == null ? "" : weekData.dates[fieldName];
                        const formattedServerValue = formatNumericValue(rawServerValue);
                        mergedField[fieldName] = formattedServerValue;
                    }
                });

                // Final safety: ensure no null values are stored in the field map
                Object.keys(mergedField).forEach((fieldName) => {
                    if (mergedField[fieldName] == null) {
                        mergedField[fieldName] = "";
                    } else if (typeof mergedField[fieldName] !== "string") {
                        mergedField[fieldName] = String(mergedField[fieldName]);
                    }
                });

                return mergedField as IWeekData["dates"];
            });

            setDayTotal(weekData.sum);

            // Keep month/year project totals in sync with the viewed week (Monday's calendar month)
            const weekMonday = new Date(week[1].valueOf());
            const totalsYear = weekMonday.getUTCFullYear();
            const totalsMonth = weekMonday.getUTCMonth() + 1;
            try {
                const totalsResponse = await getProjectHourTotals(props.token, totalsYear, totalsMonth);
                setProjectHourTotals(totalsResponse.totals || {});
                setTotalsPeriod({ year: totalsResponse.year, month: totalsResponse.month });
            } catch (totalsError) {
                console.error("Failed to fetch project hour totals", totalsError);
            }

            // Refresh default-hours day state; reload project map only on week change (cached otherwise)
            await refreshDefaultHoursDays(week);
            if (options?.loadDefaultProjectConfig) {
                await loadDefaultProjectMapForWeek(week, true);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Handle real-time time entry changes
    // Using useCallback with ref to avoid unnecessary WebSocket reconnections
    const fetchTimeEntriesRef = useRef(fetchTimeEntries);
    const isFetchingRef = useRef(false);
    const debounceTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        fetchTimeEntriesRef.current = fetchTimeEntries;
    }, [fetchTimeEntries]);

    const handleTimeEntryChange = useCallback(
        (event: { type: "created" | "updated" | "deleted"; timeEntry: ITimeEntry | null; userId: string }) => {
            // Mark the cell as changed if it's in the current week view
            if (event.timeEntry && selectedDays && selectedDays.length > 0) {
                const weekStart = selectedDays[1].valueOf();
                const weekEnd = selectedDays[8].valueOf();
                const entryTime = event.timeEntry.from;

                // Only mark if the entry is within the current week
                if (entryTime >= weekStart && entryTime < weekEnd) {
                    const cellKey = `${event.timeEntry.project_id}-${entryTime}`;
                    console.log("Marking cell as changed:", cellKey);
                    setChangedCells((prev) => ({ ...prev, [cellKey]: true }));

                    // Clear the mark after 3 seconds (visual feedback)
                    setTimeout(() => {
                        setChangedCells((prev) => {
                            const updated = { ...prev };
                            delete updated[cellKey];
                            return updated;
                        });
                    }, 3000);
                }
            }

            // Handle different event types
            // For deleted events, always refetch regardless of timeEntry (deleted entries might still have timeEntry data)
            if (event.type === "deleted") {
                if (!selectedDays || selectedDays.length === 0) {
                    console.log("No selected days, skipping refetch for deleted event");
                    return;
                }
                // Continue to refetch logic below
            } else if (event.type === "updated" || event.type === "created") {
                // For created/updated events, check if the entry is in the current week view
                if (!event.timeEntry) {
                    console.log("No timeEntry in created/updated event, skipping refetch");
                    return;
                }

                if (selectedDays && selectedDays.length > 0) {
                    const weekStart = selectedDays[1].valueOf();
                    const weekEnd = selectedDays[8].valueOf();
                    const entryTime = event.timeEntry.from;

                    // console.log("Checking week range:", {
                    //     entryTime,
                    //     weekStart,
                    //     weekEnd,
                    //     isBefore: entryTime < weekStart,
                    //     isAfter: entryTime >= weekEnd,
                    //     entryDate: new Date(entryTime).toISOString(),
                    //     weekStartDate: new Date(weekStart).toISOString(),
                    //     weekEndDate: new Date(weekEnd).toISOString(),
                    // });

                    // Only refetch week grid if the entry is within the current week;
                    // still refresh month/year project totals when the entry is in the viewed year.
                    if (entryTime < weekStart || entryTime >= weekEnd) {
                        const weekMonday = new Date(weekStart);
                        const viewedYear = weekMonday.getUTCFullYear();
                        const viewedMonth = weekMonday.getUTCMonth() + 1;
                        const entryYear = new Date(entryTime).getUTCFullYear();
                        if (entryYear === viewedYear) {
                            void getProjectHourTotals(props.token, viewedYear, viewedMonth)
                                .then((totalsResponse) => {
                                    setProjectHourTotals(totalsResponse.totals || {});
                                    setTotalsPeriod({ year: totalsResponse.year, month: totalsResponse.month });
                                })
                                .catch((err) => console.error("Failed to refresh project hour totals", err));
                        }
                        console.log("Time entry change is outside current week view, skipping week refetch");
                        return;
                    }
                } else {
                    // No selectedDays, skip
                    console.log("No selected days, skipping refetch");
                    return;
                }
            } else {
                // Unknown event type, skip
                console.log("Unknown event type, skipping refetch");
                return;
            }

            // If already fetching, skip scheduling a new request
            if (isFetchingRef.current) {
                console.log("Fetch already in progress, skipping duplicate request");
                return;
            }

            // Clear any existing debounce timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }

            // Debounce rapid changes (wait 200ms to batch multiple changes)
            debounceTimeoutRef.current = window.setTimeout(() => {
                // Double-check we're not already fetching (in case another event came in)
                if (isFetchingRef.current) {
                    console.log("Fetch started during debounce, skipping duplicate request");
                    return;
                }

                if (selectedDays && selectedDays.length > 0) {
                    console.log("Scheduling refetch for event type:", event.type);
                    isFetchingRef.current = true;
                    fetchTimeEntriesRef.current(selectedDays).finally(() => {
                        isFetchingRef.current = false;
                        console.log("Refetch completed for event type:", event.type);
                    });
                } else {
                    console.log("No selectedDays, cannot refetch");
                }
            }, 200);
        },
        [selectedDays, props.token, getProjectHourTotals],
    );

    // Initialize real-time WebSocket connection
    const { isConnected, reconnect } = useRealtimeTimeEntries({
        token: props.token,
        onTimeEntryChange: handleTimeEntryChange,
        onUserSettingsChange: (updatedUser) => {
            // Keep local user state in sync across tabs
            setUser(updatedUser);

            // Sync visible projects from app_settings so newly added/removed projects
            // appear in all open windows without a manual reload
            const updatedVisible = updatedUser.app_settings?.visible_projects;
            if (updatedVisible) {
                setVisibleProjects(updatedVisible);
                _fetchProjects(updatedVisible);
            }

            // Also keep the "show only projects with hours" flag in sync
            if (typeof updatedUser.app_settings?.show_only_projects_with_hours === "boolean") {
                setShowOnlyWithHours(updatedUser.app_settings.show_only_projects_with_hours);
            }

            invalidateDefaultProjectConfigCache();
            if (selectedDays && selectedDays.length > 0) {
                void loadDefaultProjectMapForWeek(selectedDays, true);
            }
        },
        enabled: !isLoading && !!props.token,
    });

    // Proactively try to recover the real-time connection when the user
    // returns to the app after it has been in the background (e.g. Teams left
    // open overnight) or the network comes back online.
    useEffect(() => {
        const handleVisibilityOrFocus = () => {
            if (!document.hidden && !isConnected) {
                reconnect();
            }
        };

        const handleOnline = () => {
            if (!isConnected) {
                reconnect();
            }
        };

        window.addEventListener("focus", handleVisibilityOrFocus);
        document.addEventListener("visibilitychange", handleVisibilityOrFocus);
        window.addEventListener("online", handleOnline);

        return () => {
            window.removeEventListener("focus", handleVisibilityOrFocus);
            document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
            window.removeEventListener("online", handleOnline);
        };
    }, [isConnected, reconnect]);

    // When table data is fully ready (initial load or after any refresh),
    // capture a stable snapshot. During week changes we render this snapshot
    // under the loading overlay so the user does not see partially updated rows.
    useEffect(() => {
        if (!isLoading && !changingWeek && selectedDays.length > 0 && projectsData && dropdownArr) {
            setStableTableSnapshot({
                dayTotal,
                dbHours,
                weekTotal,
                field,
                selectedDays,
                projectsData,
                dropdownArr,
                projectsArr,
            });
        }
    }, [
        isLoading,
        changingWeek,
        selectedDays,
        projectsData,
        dropdownArr,
        dayTotal,
        dbHours,
        weekTotal,
        field,
        projectsArr,
    ]);

    const handleWeekChange = async (week?: any[]) => {
        // Show overlay while we transition between weeks
        setChangingWeek(true);
        try {
            if (week) {
                // Clear focused cells when changing weeks (cell keys will be different)
                focusedCellsRef.current = {};
                localChangesRef.current = {};
                setChangedCells({});
                invalidateDefaultProjectConfigCache();
                await fetchTimeEntries(week, { loadDefaultProjectConfig: true });
                setSelectedDays(week);
                // setLoadingCells({});
            }
        } catch (error) {
            console.error("Failed to change week", error);
        } finally {
            // Always clear the overlay, even if there was an error fetching data
            setChangingWeek(false);
        }
    };

    // Get previous week days
    const handlePrev = () => {
        handleWeekChange(getWeek(selectedDays[0], "prev"));
    };

    // Get next week days
    const handleNext = () => {
        handleWeekChange(getWeek(selectedDays[0], "next"));
    };

    // Get current week days
    const handleGoToday = () => {
        handleWeekChange(getThisWeek(new Date()));
    };

    // Return WeekTable Object
    // console.log("WeekTable render state:", { isLoading, user: !!user, selectedDays, projectsArr, dropdownArr });

    return (
        <>
            {isLoading || !user ? (
                <CustomLoader msg="Fetching data" />
            ) : (
                <div className="w-full max-w-full overflow-hidden text-(--colorNeutralForeground1)">
                    {/* Real-time connection status indicator */}
                    {!isConnected && (
                        <div className="mb-2 px-2 py-1 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded flex items-center gap-2 justify-between">
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                                <span>Real-time updates disconnected. Reconnecting...</span>
                            </div>
                            <Button
                                size="small"
                                appearance="secondary"
                                onClick={() => reconnect()}
                                disabled={isConnected}
                            >
                                Retry now
                            </Button>
                        </div>
                    )}
                    <div className="w-full mb-2 px-2 sm:px-0">
                        <div className="relative w-full md:max-w-7xl md:mx-auto flex flex-row flex-wrap items-center justify-center gap-2">
                            <WeektableNavigation
                                getThisWeek={getThisWeek}
                                handleGoToday={handleGoToday}
                                handleNext={handleNext}
                                handlePrev={handlePrev}
                                handleWeekChange={handleWeekChange}
                                selectedDays={selectedDays}
                                // Week options (cogwheel) available on both desktop and mobile
                                onOpenWeekOptions={() => setIsWeekOptionsDialogOpen(true)}
                            />
                        </div>
                        {/* Debug: simulate expired token for time-entry flow */}
                        {process.env.REACT_APP_ENABLE_TOKEN_DEBUG === "true" && (
                            <div className="w-full md:max-w-7xl md:mx-auto flex justify-end px-2 sm:px-0 mt-1">
                                <Button appearance="secondary" size="small" onClick={() => void debugSimulateTokenExpired(props.token)}>
                                    Arm token expiry on next save (debug)
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="relative w-full max-w-full overflow-hidden md:overflow-visible text-(--colorNeutralForeground1) min-h-[320px]">
                        {/* Determine which data set to render:
                            - while changingWeek, keep showing the last stable snapshot
                            - otherwise, show the latest live data */}
                        {((!isLoading && projectsData && dropdownArr && selectedDays.length > 0) || stableTableSnapshot) && (
                            (() => {
                                const useSnapshot = changingWeek && stableTableSnapshot;
                                const effectiveDayTotal = useSnapshot ? useSnapshot.dayTotal : dayTotal;
                                const effectiveDbHours = useSnapshot ? useSnapshot.dbHours : dbHours;
                                const effectiveWeekTotal = useSnapshot ? useSnapshot.weekTotal : weekTotal;
                                const effectiveField = useSnapshot ? useSnapshot.field : field;
                                const effectiveSelectedDays = useSnapshot ? useSnapshot.selectedDays : selectedDays;
                                const effectiveProjectsData = useSnapshot ? useSnapshot.projectsData : projectsData;
                                const effectiveDropdownArr = useSnapshot ? useSnapshot.dropdownArr : dropdownArr;
                                const effectiveProjectsArr = useSnapshot ? useSnapshot.projectsArr : projectsArr;

                                if (!effectiveProjectsData || !effectiveDropdownArr || effectiveSelectedDays.length === 0) {
                                    return null;
                                }

                                return (
                                <WeektableDataRows
                                    dayTotal={effectiveDayTotal}
                                    dbHours={effectiveDbHours}
                                    jwtoken={props.token}
                                    weekTotal={effectiveWeekTotal}
                                    field={effectiveField}
                                    overflow={overflow}
                                    selectedDays={effectiveSelectedDays}
                                    setVisibleProjects={setVisibleProjects}
                                    user={user!}
                                    visibleProjects={visibleProjects}
                                    dropdownArr={effectiveDropdownArr}
                                    projectsArr={effectiveProjectsArr}
                                    projectsData={effectiveProjectsData}
                                    loadingCells={loadingCells}
                                    setField={setFieldWithRef}
                                    fetchTimeEntries={fetchTimeEntries}
                                    setDropdownArr={setDropdownArr}
                                    setProjectsData={setProjectsData}
                                    setLoadingCells={setLoadingCells}
                                    update={update}
                                    focusedCellsRef={focusedCellsRef}
                                    fieldRef={fieldRef}
                                    changedCells={changedCells}
                                    localChangesRef={localChangesRef}
                                    onQuickFillSuccess={(updatedFieldNames) => {
                                        // Clear local change tracking for updated fields and update field state
                                        if (updatedFieldNames && updatedFieldNames.length > 0) {
                                            updatedFieldNames.forEach((fieldName) => {
                                                delete localChangesRef.current[fieldName];
                                            });
                                            // Update field state to clear old values so merge logic uses server values
                                            setFieldWithRef((prevField) => {
                                                if (!prevField) return prevField;
                                                const updatedField = { ...prevField };
                                                updatedFieldNames.forEach((fieldName) => {
                                                    // Remove the field so merge logic will use server value
                                                    delete updatedField[fieldName];
                                                });
                                                return updatedField;
                                            });
                                        }
                                        // Refresh time entries after quick fill
                                        if (selectedDays && selectedDays.length > 0) {
                                            fetchTimeEntries(selectedDays);
                                        }
                                    }}
                                    bulkFillOnSuccess={(updatedFieldNames) => {
                                        // Clear local change tracking for updated fields and update field state
                                        if (updatedFieldNames && updatedFieldNames.length > 0) {
                                            updatedFieldNames.forEach((fieldName) => {
                                                delete localChangesRef.current[fieldName];
                                            });
                                            // Update field state to clear old values so merge logic uses server values
                                            setFieldWithRef((prevField) => {
                                                if (!prevField) return prevField;
                                                const updatedField = { ...prevField };
                                                updatedFieldNames.forEach((fieldName) => {
                                                    // Remove the field so merge logic will use server value
                                                    delete updatedField[fieldName];
                                                });
                                                return updatedField;
                                            });
                                        }
                                        // Refresh time entries after bulk fill
                                        if (selectedDays && selectedDays.length > 0) {
                                            fetchTimeEntries(selectedDays);
                                        }
                                    }}
                                    defaultHoursByDate={defaultHoursByDate}
                                    defaultProjectIdByDate={defaultProjectIdByDate}
                                    onDefaultHoursDayUpdated={handleDefaultHoursDayUpdatedFromTimeEntry}
                                    showOnlyWithHours={showOnlyWithHours}
                                    setShowOnlyWithHours={setShowOnlyWithHours}
                                    projectHourTotals={projectHourTotals}
                                    totalsPeriod={totalsPeriod}
                                />
                                );
                            })()
                        )}
                        {(isLoading || changingWeek) && <SpinnerOverlay />} {/* Conditionally render the spinner overlay */}
                        {/* Week Options Dialog */}
                        <Dialog open={isWeekOptionsDialogOpen} onOpenChange={(_, data) => !data.open && setIsWeekOptionsDialogOpen(false)}>
                            <DialogSurface>
                                <DialogBody>
                                    <DialogTitle>
                                        <Text size={400} weight="semibold">
                                            Week options
                                        </Text>
                                    </DialogTitle>
                                    <DialogContent>
                                        <div className="flex flex-col gap-4 mt-2">
                                            {/* Ring color legend (shown on demand via cogwheel) */}
                                            <div className="flex flex-col gap-1 text-[0.75rem] text-(--colorNeutralForeground2)">
                                                <Text size={300} weight="semibold">
                                                    Color legend
                                                </Text>
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-block h-3 w-5 rounded border-2 border-purple-500"></span>
                                                        <span>Default project (auto)</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-block h-3 w-5 rounded border-2 border-orange-400"></span>
                                                        <span>Default hours (manual)</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-block h-3 w-5 rounded border-2 border-cyan-400"></span>
                                                        <span>Recently updated</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-3">
                                                <Checkbox
                                                    label="Show only projects with hours"
                                                    checked={showOnlyWithHours}
                                                    onChange={async (_, data) => {
                                                        const checked = !!data.checked;
                                                        setShowOnlyWithHours(checked);
                                                        try {
                                                            // Persist preference to user app_settings
                                                            const currentUser = user;
                                                            if (currentUser && currentUser.app_settings) {
                                                                const updatedSettings = {
                                                                    ...currentUser.app_settings,
                                                                    show_only_projects_with_hours: checked,
                                                                };
                                                                const updatedUser = await updateUserAppSettings(
                                                                    props.token,
                                                                    updatedSettings,
                                                                );
                                                                setUser(updatedUser);
                                                            }
                                                        } catch (err) {
                                                            console.error("Failed to update user app settings", err);
                                                        }
                                                    }}
                                                />
                                                <div className="flex flex-col gap-1">
                                                    <Text size={300} weight="semibold">
                                                        Bulk fill hours
                                                    </Text>
                                                    <Text size={200} className="text-(--colorNeutralForeground3)">
                                                        Open the bulk fill dialog to quickly fill hours for the week.
                                                    </Text>
                                                    <BulkFillHoursDialog
                                                        token={props.token}
                                                        user={user!}
                                                        projectsData={projectsData}
                                                        onSuccess={(updatedFieldNames) => {
                                                            if (updatedFieldNames && updatedFieldNames.length > 0) {
                                                                updatedFieldNames.forEach((fieldName) => {
                                                                    delete localChangesRef.current[fieldName];
                                                                });
                                                                setFieldWithRef((prevField) => {
                                                                    if (!prevField) return prevField;
                                                                    const updatedField = { ...prevField };
                                                                    updatedFieldNames.forEach((fieldName) => {
                                                                        delete updatedField[fieldName];
                                                                    });
                                                                    return updatedField;
                                                                });
                                                            }
                                                            if (selectedDays && selectedDays.length > 0) {
                                                                fetchTimeEntries(selectedDays);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button appearance="secondary" onClick={() => setIsWeekOptionsDialogOpen(false)}>
                                            Close
                                        </Button>
                                    </DialogActions>
                                </DialogBody>
                            </DialogSurface>
                        </Dialog>
                    </div>
                </div>
            )}
        </>
    );
};
export default WeekTable;
